import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import clsx from 'clsx';
import styles from './userReviewSlider.module.css';
import avatarPlaceholder from '../../../assets/images/verticaltv/Ellipse-22509.png';

export interface Review {
  id: number;
  name: string;
  location: string;
  rating: number;
  text: string;
  avatar?: string;
}

interface UserReviewSliderProps {
  reviews: Review[];
  onReviewChange?: (review: Review) => void;
}

const UserReviewSlider: React.FC<UserReviewSliderProps> = React.memo(({ reviews, onReviewChange }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const onReviewChangeRef = useRef(onReviewChange);

  // Keep the ref updated with the latest callback
  useEffect(() => {
    onReviewChangeRef.current = onReviewChange;
  }, [onReviewChange]);

  // Call onReviewChange whenever currentIndex changes
  useEffect(() => {
    if (reviews.length > 0 && onReviewChangeRef.current) {
      const currentReview = reviews[currentIndex];
      onReviewChangeRef.current(currentReview);
    }
  }, [currentIndex, reviews.length]);

  //Auto-play: automatically cycle through reviews
  useEffect(() => {
    if (reviews.length <= 1) {
      return; // Don't auto-play if there's only one or no reviews
    }
    
    const autoPlayInterval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        // Move to next review, loop back to first after last
        return (prevIndex + 1) % reviews.length;
      });
    }, 3000); // Change review every 3 seconds

    return () => {
      clearInterval(autoPlayInterval);
    };
  }, [reviews.length]);

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    const startX = touch.clientX;
    
    const handleTouchEnd = (e: React.TouchEvent) => {
      const touch = e.changedTouches[0];
      const endX = touch.clientX;
      const diff = startX - endX;

      if (Math.abs(diff) > 50) {
        if (diff > 0 && currentIndex < reviews.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else if (diff < 0 && currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
        }
      }

      document.removeEventListener('touchend', handleTouchEnd as any);
    };

    document.addEventListener('touchend', handleTouchEnd as any);
  };

  if (reviews.length === 0) {
    return null;
  }

  const currentReview = reviews[currentIndex];

  return (
    <div className={styles.reviewSection} onTouchStart={handleTouchStart}>
      <div className={styles.reviewHeader}>
        <div className={styles.reviewProfile}>
          <div className={styles.reviewAvatar}>
            {/* Pre-render all images but only show the current one */}
            {reviews.map((review, index) => {
              const isActive = index === currentIndex;
              return review.avatar ? (
                <img 
                  key={review.id}
                  src={review.avatar} 
                  alt={review.name}
                  loading="eager"
                  className={isActive ? styles.avatarVisible : styles.avatarHidden}
                />
              ) : (
                <div 
                  key={review.id}
                  className={clsx(styles.avatarPlaceholder, !isActive && styles.avatarHidden)}
                >
                  <img 
                    src={avatarPlaceholder} 
                    alt={review.name}
                    className={styles.avatarPlaceholderImage}
                    loading="eager"
                  />
                </div>
              );
            })}
          </div>
          <div className={styles.reviewUserInfo}>
            <span className={styles.reviewName}>{currentReview.name}</span>
            <span className={styles.reviewLocation}>{currentReview.location}</span>
          </div>
        </div>
        <div className={styles.reviewRating}>
          {[...Array(5)].map((_, i) => (
            <svg
              key={i}
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 0L10.163 5.528L16 6.112L12 10.056L12.944 16L8 13.056L3.056 16L4 10.056L0 6.112L5.837 5.528L8 0Z"
                fill={i < currentReview.rating ? "#FFD700" : "none"}
                stroke="#FFD700"
                strokeWidth={i < currentReview.rating ? "0" : "1"}
              />
            </svg>
          ))}
        </div>
      </div>
      <p className={styles.reviewText}>{currentReview.text}</p>
      <div className={styles.reviewPagination}>
        {reviews.map((_, index) => (
          <span
            key={index}
            className={clsx(
              styles.paginationDot,
              index === currentIndex && styles.paginationDotActive
            )}
            onClick={() => handleDotClick(index)}
          ></span>
        ))}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if reviews array reference changes
  // This prevents unnecessary re-renders when props haven't actually changed
  return prevProps.reviews === nextProps.reviews && 
         prevProps.onReviewChange === nextProps.onReviewChange;
});

UserReviewSlider.displayName = 'UserReviewSlider';

export default UserReviewSlider;

